import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowUpDown, Search, Package } from "lucide-react";

function resolveApiUrl() {
  const envApiUrl = import.meta.env.VITE_API_URL as string | undefined;
  const fallback = `${window.location.protocol}//${window.location.hostname}:4000`;
  if (!envApiUrl) return fallback;
  try {
    const u = new URL(envApiUrl);
    if (u.hostname === "localhost" || u.hostname === "127.0.0.1") return fallback;
    return envApiUrl;
  } catch {
    return envApiUrl || fallback;
  }
}

const MALAYSIA_TZ = "Asia/Kuala_Lumpur";
const STOCK_LAST_GROUP_KEY = "stockLastProductGroupName";
const STOCK_IMAGE_CACHE_NAME = "stock-product-images-v1";

function malaysiaDateKeyFromDate(d: Date) {
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: MALAYSIA_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const parts = fmt.formatToParts(d);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

function malaysiaDateLabelFromDate(d: Date) {
  // Example: 04-Mar-2026
  const fmt = new Intl.DateTimeFormat("en-GB", {
    timeZone: MALAYSIA_TZ,
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  const label = fmt.format(d); // e.g. "04 Mar 2026"
  return label.replace(/\s+/g, "-");
}

export default function Stock() {
  const navigate = useNavigate();
  const apiUrl = useMemo(() => resolveApiUrl(), []);
  const token = localStorage.getItem("token") || "";

  const [loadingGroups, setLoadingGroups] = useState(false);
  const [loadingStock, setLoadingStock] = useState(false);
  const [error, setError] = useState("");
  const [productGroupNames, setProductGroupNames] = useState<string[]>([]);
  const [activeGroupName, setActiveGroupName] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState("");

  // Date-only selection (GMT+8). Submit not applicable here; reads use selected date.
  const todayUTC = useMemo(() => {
    const key = malaysiaDateKeyFromDate(new Date());
    const [y, m, dd] = key.split("-").map((x) => Number(x));
    return new Date(Date.UTC(y, m - 1, dd));
  }, []);
  const [daysBack, setDaysBack] = useState(0);
  const selectedUTCDate = useMemo(() => {
    const d = new Date(todayUTC);
    d.setUTCDate(d.getUTCDate() - daysBack);
    return d;
  }, [todayUTC, daysBack]);
  const selectedDateKey = useMemo(() => malaysiaDateKeyFromDate(selectedUTCDate), [selectedUTCDate]);
  const isToday = daysBack === 0;

  type StockProductRow = {
    productId: string;
    name: string;
    sku: string | null;
    productImageUrl?: string | null;
    productImageVersion?: string | null;
    openingQty: number;
    closingQty: number;
  };
  type StockGroupRow = { groupName: string; products: StockProductRow[] };
  type TeamStockResponse = {
    team: { id: string; name: string } | null;
    groups: StockGroupRow[];
  };

  const [stockData, setStockData] = useState<TeamStockResponse | null>(null);
  const productImageBlobUrlsRef = useRef<Map<string, string>>(new Map());
  const [cachedImageSrcByProductId, setCachedImageSrcByProductId] = useState<Record<string, string>>({});
  const [brokenImageProductIds, setBrokenImageProductIds] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const load = async () => {
      if (!token) {
        setError("Not authenticated. Please login again.");
        return;
      }

      setLoadingGroups(true);
      setError("");
      try {
        const res = await fetch(`${apiUrl}/supervisor/product-groups`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const json: { groups: string[] } = await res.json().catch(() => ({ groups: [] }));
        if (!res.ok) {
          setError((json as any)?.message || `Failed to load balances (${res.status}).`);
          return;
        }

        setProductGroupNames(json.groups ?? []);
        const groups = json.groups ?? [];
        const remembered = localStorage.getItem(STOCK_LAST_GROUP_KEY) || "";
        const nextActive = groups.includes(remembered) ? remembered : groups[0] ?? "";
        setActiveGroupName(nextActive);
      } catch {
        setError("Unable to connect to server. Please try again.");
      } finally {
        setLoadingGroups(false);
      }
    };

    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const loadStock = async () => {
      if (!token) return;
      setLoadingStock(true);
      try {
        const res = await fetch(`${apiUrl}/supervisor/team-stock?date=${selectedDateKey}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const json: TeamStockResponse = await res.json().catch(() => ({ team: null, groups: [] }));
        if (!res.ok) {
          setError((json as any)?.message || `Failed to load stock (${res.status}).`);
          return;
        }
        setError("");
        setStockData(json);
        setBrokenImageProductIds({});
      } catch {
        setError("Unable to connect to server. Please try again.");
      } finally {
        setLoadingStock(false);
      }
    };

    void loadStock();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDateKey]);

  useEffect(() => {
    return () => {
      productImageBlobUrlsRef.current.forEach((blobUrl) => URL.revokeObjectURL(blobUrl));
      productImageBlobUrlsRef.current.clear();
    };
  }, []);

  useEffect(() => {
    const allProducts = (stockData?.groups ?? []).flatMap((g) => g.products ?? []);
    const activeIds = new Set(allProducts.map((p) => p.productId));

    const removedIds: string[] = [];
    productImageBlobUrlsRef.current.forEach((blobUrl, productId) => {
      if (!activeIds.has(productId)) {
        URL.revokeObjectURL(blobUrl);
        productImageBlobUrlsRef.current.delete(productId);
        removedIds.push(productId);
      }
    });
    if (removedIds.length > 0) {
      setCachedImageSrcByProductId((prev) => {
        const next = { ...prev };
        removedIds.forEach((id) => delete next[id]);
        return next;
      });
    }

    if (!("caches" in window) || allProducts.length === 0) return;

    let cancelled = false;
    const loadAndCacheImages = async () => {
      const cache = await caches.open(STOCK_IMAGE_CACHE_NAME);

      for (const product of allProducts) {
        if (cancelled) return;

        const imageUrl = product.productImageUrl ?? "";
        const imageVersion = product.productImageVersion ?? imageUrl;
        if (!imageUrl || !imageVersion) continue;

        const cacheKey = `${window.location.origin}/__stock_product_image_cache__?productId=${encodeURIComponent(
          product.productId
        )}&v=${encodeURIComponent(imageVersion)}`;

        try {
          let response = await cache.match(cacheKey);
          if (!response) {
            const fetched = await fetch(imageUrl, { cache: "force-cache" });
            if (!fetched.ok) continue;
            const freshBlob = await fetched.blob();
            response = new Response(freshBlob, {
              headers: { "Content-Type": freshBlob.type || "image/jpeg" },
            });
            await cache.put(cacheKey, response.clone());
          }

          const blob = await response.blob();
          if (cancelled) return;

          const oldBlobUrl = productImageBlobUrlsRef.current.get(product.productId);
          if (oldBlobUrl) URL.revokeObjectURL(oldBlobUrl);

          const blobUrl = URL.createObjectURL(blob);
          productImageBlobUrlsRef.current.set(product.productId, blobUrl);
          setCachedImageSrcByProductId((prev) => ({ ...prev, [product.productId]: blobUrl }));
        } catch {
          // Keep placeholder if image fetch/cache fails.
        }
      }
    };

    void loadAndCacheImages();

    return () => {
      cancelled = true;
    };
  }, [stockData]);

  const activeGroup = useMemo(() => {
    const g = stockData?.groups?.find((x) => x.groupName === activeGroupName);
    return g ?? null;
  }, [stockData, activeGroupName]);

  const filteredProducts = useMemo(() => {
    const list = activeGroup?.products ?? [];
    const q = searchQuery.trim().toLowerCase();
    if (!q) return list;
    return list.filter((p) => (p.name ?? "").toLowerCase().includes(q) || (p.sku ?? "").toLowerCase().includes(q));
  }, [activeGroup, searchQuery]);

  useEffect(() => {
    if (!activeGroupName) return;
    localStorage.setItem(STOCK_LAST_GROUP_KEY, activeGroupName);
  }, [activeGroupName]);

  return (
    <div className="min-h-[calc(100vh-60px)] bg-white">
      {/* Gradient Header */}
      <div className="h-[100px] bg-gradient-to-r from-theme-1/95 to-theme-2/95 text-white relative">
        <div aria-hidden className="absolute inset-0 bg-texture-white opacity-20 pointer-events-none" />
        <div className="relative z-10 h-full px-4 pt-3">
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => navigate("/app")}
              className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center"
              aria-label="Back"
            >
              <ArrowLeft size={24} className="text-white" />
            </button>

            <div className="text-[20px] font-semibold">Stock</div>

            <button
              type="button"
              className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center"
              aria-label="Sort (disabled for now)"
            >
              <ArrowUpDown size={20} className="text-white" />
            </button>
          </div>
        </div>
      </div>

      {/* Date bar */}
      <div className="px-4 pt-1 pb-1">
        <div className="bg-white border border-gray-200 rounded-xl px-2 py-1 flex items-center justify-between">
          <button
            type="button"
            className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-gray-200 bg-white text-sm text-slate-700 hover:bg-gray-50"
            onClick={() => setDaysBack((n) => n + 1)}
            aria-label="Go to previous date"
          >
            <span className="font-semibold leading-none">&lt;</span>
          </button>

          <div className="flex-1 flex justify-center px-2">
            <div className="text-center">
              <div className="text-sm font-semibold text-slate-900 truncate leading-4">
                {isToday ? "Today" : malaysiaDateLabelFromDate(selectedUTCDate)}
              </div>
            </div>
          </div>

          <button
            type="button"
            className={`inline-flex items-center justify-center w-8 h-8 rounded-md border ${
              isToday ? "border-gray-200 bg-white text-sm text-slate-400 cursor-not-allowed" : "border-gray-200 bg-white text-slate-700 hover:bg-gray-50"
            }`}
            disabled={isToday}
            aria-label="Go to next date (toward today)"
            onClick={() => setDaysBack((n) => Math.max(0, n - 1))}
          >
            <span className="font-semibold leading-none">&gt;</span>
          </button>
        </div>
      </div>

      {/* Tab Buttons */}
      <div className="px-4 pt-0 pb-1 bg-gray-50">
        <div
          className="bg-gray-100 rounded-lg p-[4px] overflow-x-auto
          [&::-webkit-scrollbar]:h-1
          [&::-webkit-scrollbar-track]:bg-transparent
          [&::-webkit-scrollbar-thumb]:rounded-full
          [&::-webkit-scrollbar-thumb]:bg-theme-1/45
          hover:[&::-webkit-scrollbar-thumb]:bg-theme-1/60"
        >
          <div className="flex w-max min-w-full gap-1">
            {productGroupNames.map((groupName) => {
              const active = groupName === activeGroupName;
              return (
                <button
                  key={groupName}
                  type="button"
                  className={`shrink-0 py-2 px-3 rounded-md text-sm font-medium whitespace-nowrap ${
                    active
                      ? "bg-white text-primary shadow-[0_2px_4px_rgba(0,0,0,0.08)]"
                      : "text-gray-600"
                  }`}
                  onClick={() => setActiveGroupName(groupName)}
                >
                  {groupName}
                </button>
              );
            })}
            {loadingGroups && productGroupNames.length === 0 ? (
              <div className="py-2 px-3 text-xs text-gray-500 animate-pulse">Loading...</div>
            ) : null}
            {!loadingGroups && productGroupNames.length === 0 ? <div className="py-2 px-3 text-xs text-gray-500">No groups</div> : null}
          </div>
        </div>
      </div>

      {/* Product List (placeholder for now) */}
      {error ? (
        <div className="px-4 pt-2">
          <div className="rounded-lg border border-red-200 bg-red-50 text-red-700 px-3 py-2 text-sm mb-2">{error}</div>
        </div>
      ) : null}

      {loadingStock && !stockData ? (
        <div className="bg-gray-50 px-4 pt-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-12 rounded-lg bg-white border border-gray-200 animate-pulse mb-2" />
          ))}
        </div>
      ) : null}

      {!loadingStock ? (
        <div className="bg-gray-50 px-4 pt-4 pb-24">
          {!activeGroupName ? null : (
            <div className="mb-3 flex items-center gap-2">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-slate-900 truncate">{activeGroupName}</div>
                <div className="text-xs text-slate-500 mt-0.5">{filteredProducts.length} item(s)</div>
              </div>
              <div className="w-[180px] max-w-[50%]">
                <div className="flex items-center bg-white rounded-lg border border-gray-200 h-9 px-2">
                  <Search size={16} className="text-gray-400 mr-1.5 shrink-0" />
                  <input
                    type="text"
                    className="flex-1 outline-none border-0 shadow-none rounded-none p-0 appearance-none text-sm text-slate-900 placeholder:text-gray-400 bg-transparent min-w-0"
                    placeholder="Search products..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>
            </div>
          )}

          {filteredProducts.length ? (
            <div className="pb-4">
              {filteredProducts.map((p) => {
                const imageSrc = cachedImageSrcByProductId[p.productId] || p.productImageUrl || null;
                const showImage = Boolean(imageSrc) && !brokenImageProductIds[p.productId];
                return (
                  <div
                    key={p.productId}
                    className="rounded-xl border border-primary/40 shadow-[0_2px_8px_rgba(0,0,0,0.08)] mb-2 overflow-hidden bg-white"
                  >
                    <div className="flex flex-row p-3 bg-white">
                      <div className="w-[50px] h-[50px] rounded-lg bg-primary/5 border border-primary/30 flex items-center justify-center shrink-0">
                        {showImage ? (
                          <img
                            src={imageSrc!}
                            alt={p.name}
                            loading="lazy"
                            className="w-full h-full rounded-lg object-contain bg-white"
                            onError={() =>
                              setBrokenImageProductIds((prev) => ({
                                ...prev,
                                [p.productId]: true,
                              }))
                            }
                          />
                        ) : (
                          <Package size={20} className="text-gray-400" />
                        )}
                      </div>

                      <div className="flex-1 ml-3 mr-3 flex flex-col justify-between min-w-0">
                        <div>
                          <div className="text-sm font-semibold text-slate-900 leading-5 line-clamp-3">{p.name}</div>
                          {p.sku ? (
                            <div className="mt-1 text-xs text-slate-500">
                              SKU: <span className="text-xs font-medium text-slate-700">{p.sku}</span>
                            </div>
                          ) : null}
                        </div>

                        <div className="mt-3 flex items-end justify-between gap-4">
                          <div className="flex flex-col">
                            <div className="text-[11px] font-medium text-slate-600 mb-1">Opening</div>
                            <div className="text-sm font-semibold text-slate-900 tabular-nums">{p.openingQty.toLocaleString()}</div>
                          </div>
                          <div className="flex flex-col text-right">
                            <div className="text-[11px] font-medium text-slate-600 mb-1">Closing</div>
                            <div className="text-sm font-semibold text-slate-900 tabular-nums">{p.closingQty.toLocaleString()}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center text-center py-10">
              <Package size={48} className="text-gray-400" />
              <div className="mt-2 text-sm font-medium text-slate-700">No products found</div>
              {searchQuery ? <div className="mt-1 text-xs text-slate-500">Try adjusting your search</div> : null}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

